import { useNavigate, useLocation } from "react-router-dom";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import endpoints from "../../../services/endpoints";
import { TableDetailsItem } from "../../../components/tables/tableFunctions";
import { useEffect, useState, useCallback, useMemo } from "react";
import usePrivilege from "../../../components/CustomHooks/usePrivilege";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import { showToast } from "../../../components/toastMessage/toastSlice";
import useApi from "../../../services/useApi";
import { setPreviousUrl, setUserUrl } from "../../../store/slices/geolocationSlice";
import { setUserManagementType } from "../../../store/slices/activeListSlice";
import { resetPhoneNumbers } from "../../../store/slices/phoneNumbersSlice";
import GridTable from "../../../components/GridTable/GridTable";
import { DynamicFilterConfig } from "../../../components/DynamicComponent/DynamicFilterPanel";
import CustomRadioButton from "../../../components/buttons/CustomRadioButton";

interface UserManagementProps {
  type: string;
  testingProp?: boolean;
}

interface PrivilegeObject {
  add?: boolean;
  list?: boolean;
  activateDeactivate?: boolean;
  view?: boolean;
  edit?: boolean;
  flaggingDistributor?: boolean;
  distributorTransactions?: boolean;
}

export default function ListOfAdminTransactions() {
  const location = useLocation();
  const navigate = useNavigate();
  let currentUrl = location.pathname + location.search;
  const dispatch = useDispatch<AppDispatch>();
  const { profileTransactions } = endpoints;
  const [searchPlaceholder, setSearchPlaceholder] =
    useState<string>("transaction id");
  const [selectedView, setSelectedView] = useState("All");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const viewFromUrl = params.get("transaction_view") || "All";
    setSelectedView(viewFromUrl);
  }, [window.location.search]);

  const TableDetails: { [key: string]: TableDetailsItem } = {
    "Transaction ID": {
      id: "transaction_id",
      className: "min-w-[140px]",
    },
    "Transaction Date and Time": {
      sort: true,
      id: "transaction_date",
      custom: ["transaction_date"],
      className: "min-w-[250px]",
    },
    "Requested Date and Time": {
      id: "requested_date",
      custom: ["requested_date"],
      className: "min-w-[250px]",
    },
    "Transaction Type (Status)": {
      id: "transaction_category",
    },
    Wallet: {
      id: "wallet_type",
      className: "min-w-[140px]",
      custom: ["wallet_type"],
    },
    "Requested By": {
      id: "requested_by",
      className: "min-w-[240px]",
    },
    "Requested From": {
      id: "requested_from",
      className: "min-w-[240px]",
    },
    "Payment Type": {
      id: "payment_type",
      className: "min-w-[140px]",
      custom: ["payment_type"],
    },
    "Transaction Status": {
      id: "transaction_status",
    },
    Amount: {
      id: "amount",
      custom: ["amount"],
    },
    "Payable Amount": {
      id: "payable_amount",
      custom: ["payable_amount"],
    },
    Flagged: {
      id: "flagged",
      className: "min-w-[140px]",
      custom: ["flagged"],
    },
    Actions: {
      id: "actions",
      className: "min-w-[200px]",
      actionTypes: ["view"],
    },
  };

  useEffect(() => {
    dispatch(setUserUrl(null));
    dispatch(setPreviousUrl(currentUrl));
    dispatch(resetPhoneNumbers());
  }, [currentUrl]);

  const handleActions = async (id: string, key: string, text?: string) => {
    navigate(`/view-profile/transaction-details/${id}`);
  };

  const staticText = {
    emptyScreenText: {
      heading1: `There are no transactions yet`,
      heading2: ``,
    },
  };

  // Dynamic filter config for transactions
  const dynamicFilterConfig: DynamicFilterConfig = {
    "Transaction Date": {
      label: "Transaction Date",
      type: "date",
      required: false,
      searchKey: {
        start: "transaction_start_date",
        end: "transaction_end_date",
      },
    },
    "Requested Date": {
      label: "Requested Date",
      type: "date",
      required: false,
      searchKey: {
        start: "requested_start_date",
        end: "requested_end_date",
      },
    },
    "Transaction Type": {
      label: "Transaction Type",
      type: "checkbox",
      options: ["eTop Up", "Recharge", "Bundle Activation", "Reversal"],
      required: false,
      searchKey: "transaction_category",
    },
    "Wallet Type": {
      label: "Wallet Type",
      type: "checkbox",
      options: ["eTop Up", "Digital Wallet"],
      required: false,
      searchKey: "wallet_type",
    },
    "Requested By": {
      label: "Requested By",
      type: "checkbox",
      options: ["Self", "Others"],
      required: false,
      searchKey: "requested_by",
    },
    "Payment Type": {
      label: "Payment Type",
      type: "checkbox",
      options: ["Paid", "Advance"],
      required: false,
      searchKey: "payment_type",
    },
    "Payment Status": {
      label: "Payment Status",
      type: "checkbox",
      options: ["Approved", "Pending", "Rejected"],
      required: false,
      searchKey: "transaction_category_status",
    },
    "Transaction Status": {
      label: "Transaction Status",
      type: "checkbox",
      options: ["Success", "Failed"],
      required: false,
      searchKey: "transaction_status",
    },
  };

  const renderTopBarExtras = ({
    entireResponse,
    searchParams,
    setSearchParams,
    loading,
  }: any) => (
    <div className="mb-[-10px] ml-1.5">
      {loading ? (
        <div className="flex items-center gap-4 px-3 pt-2.5">
          <div className="h-8 w-20 animate-pulse bg-gray-200 rounded"></div>
          <div className="h-8 w-80 animate-pulse bg-gray-200 rounded"></div>
          <div className="h-8 w-80 animate-pulse bg-gray-200 rounded"></div>
        </div>
      ) : (
        <CustomRadioButton
          options={[
            { id: "All", label: "All" },
            {
              id: "Total Payables",
              label: `Total Payables - AFA ${Number(
                entireResponse?.total_payables
              ).toFixed(2)}`,
            },
            {
              id: "Total Receivables",
              label: `Total Receivables - AFA ${Number(
                entireResponse?.total_receivables
              ).toFixed(2)}`,
            },
          ]}
          isDisabled={[
            entireResponse?.total_payables ? "" : "Total Payables",
            entireResponse?.total_receivables ? "" : "Total Receivables",
          ]}
          selected={selectedView}
          textClasses="text-subTitle"
          gapClassName="gap-0.5"
          onChange={(val) => {
            setSelectedView(val);
            setSearchParams((prev: any) => {
              const updated = new URLSearchParams(prev);
              if (val === "All") {
                updated.delete("transaction_view");
              } else {
                updated.set("transaction_view", val);
              }
              // Reset to first page when switching views
              updated.set("page_number", "1");
              return updated;
            });
          }}
        />
      )}
    </div>
  );

  const renderSummary = ({ entireResponse, loading }: any) => (
    <div className="mt-3 flex justify-between pr-1 ">
      {loading ? (
        <>
          <div className="h-6 w-48 animate-pulse bg-gray-200 rounded"></div>
          <div className="h-6 w-48 animate-pulse bg-gray-200 rounded"></div>
        </>
      ) : (
        <>
          <div>
            No. of Transactions{" "}
            <span className="text-p_primary font-bold">{entireResponse?.total_count ?? 0}</span>
          </div>
          <div>
            Sum of Total AFA{" "}
            <span className="text-p_primary font-bold">
              {Number(entireResponse?.total_amount).toFixed(2) ?? 0}
            </span>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div>
      <Breadcrumbs
        navPath={["View Profile", `List of Transactions`]}
        heading={`List of Transactions`}
        links={["/view-profile"]}
      />
      <GridTable
        staticText={staticText}
        mainEndPoint={profileTransactions}
        TableDetails={TableDetails}
        handleActions={handleActions}
        params={{ page_number: "1", sort: "transaction_date+desc" }}
        searchPlaceholder={`Search by ${searchPlaceholder}`}
        filters={true}
        exporting={true}
        reportType="Transactions"
        notfoundRoute={{ state: { route: `/view-profile` } }}
        title={`View Transaction Details`}
        dynamicFilterConfig={dynamicFilterConfig}
        renderTopBarExtras={renderTopBarExtras}
        renderSummary={renderSummary}
        heightClassName="h-transactionListHeight"
        hasActivateDeactivateMDPrivilege={true}
        hasViewMDPrivilege={true}
        hasEditMDPrivilege={true}
        addMorePadding={true}
        customHeight="h-[130px]"
        mainDivScroll={true}
      />
    </div>
  );
}